<template>
  <div class="page-listings">
    
    <leadtimes-tour v-if="$store.state.tour && !loading" />
    
    <banner-2 
      title="Spacesaver Lead Times" 
      linkTarget="/lead-time-history"
      linkText="Lead Time History"
      loadingText="Loading Lead Times...."
      :loading="loading"
      desc="Lead times are approximate and subject to change, based on order volume and the exact product mix on your order. Please use them as a guide for planning purposes only."
    />

    <div class="container">
      <!-- ADMIN EDIT BUTTON -->
      <div v-if="$store.getters.isSpacesaverEmployee && LeadTimesEdit && !loading" class="text-end mt-3">
        <button class="btn btn-sm" @click="openEditModal" 
          style="margin-right: 5px; padding: 5px 15px; background: #4d5967; color: #fff;">
          <i class="fas fa-edit"></i> Edit Lead Times
        </button>
      </div>
    </div>

    <!-- EDIT MODAL -->
    <div v-if="showEditModal && LeadTimesEdit" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5); overflow: scroll;">
      <div class="modal-dialog modal-xl">
        <div class="modal-content" style="overflow: scroll;">
          <div class="modal-header" style="padding: 5px 15px;">
            <h5 class="modal-title" style="margin-top: 0!important;">Edit Lead Times</h5>
            <div @click="closeEditModal" style="cursor: pointer; font-weight: bold;">X</div>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <button class="btn btn-sm" @click="addNewRow" 
                style="display: flex; align-items: center; gap: 5px; background:#4d5967; border: none; color: #fff;">
                <span class="material-icons">playlist_add</span> Add New
              </button>
            </div>

            <div class="table-responsive">
              <table class="table table-striped table-bordered" style="font-size: 12px; position: relative">
                <thead class="table-dark">
                  <tr style="font-size: 13px;">
                    <th style="width: 20%;">Desc</th>
                    <th style="width: 20%;">Footnote</th>
                    <th>Category</th>
                    <th>Family</th>
                    <th>Configura Type</th>
                    <th>Lead Time (weeks)</th>
                    <th>Is Viewable</th>
                    <th>Call For Lead Time</th>
                    <th style="width: 140px;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(item, index) in editedLeadTimes" 
                    :key="item.id ?? `temp-${index}`"
                    :class="{ 'editing-row': editingRowId === (item.id ?? 'new') }"
                  >
                    <!-- Editing mode -->
                    <template v-if="editingRowId === (item.id ?? 'new')">
                      <td>
                        <textarea 
                          v-model="item.productDescription" 
                          class="form-control form-control-sm"
                          rows="2"
                          style="resize: vertical; min-height: 60px; font-size: 12px;"
                          @keydown.enter="saveRow(item)"
                        ></textarea>
                      </td>
                      <td>
                        <textarea 
                          v-model="item.footnote" 
                          class="form-control form-control-sm"
                          rows="2"
                          style="resize: vertical; min-height: 60px; font-size: 12px;"
                          @keydown.enter="saveRow(item)"
                        ></textarea>
                      </td>
                      <td>
                        <input 
                          v-model="item.productCategory" 
                          class="form-control form-control-sm" 
                          style="font-size: 12px;"
                          @keydown.enter="saveRow(item)"
                        >
                      </td>
                      <td>
                        <input 
                          v-model="item.productFamily" 
                          class="form-control form-control-sm" 
                          style="font-size: 12px;"
                          @keydown.enter="saveRow(item)"
                        >
                      </td>
                      <td>
                        <input 
                          v-model.number="item.configuraType" 
                          type="number" 
                          class="form-control form-control-sm" 
                          style="font-size: 12px;"
                          @keydown.enter="saveRow(item)"
                        >
                      </td>
                      <td>
                        <input 
                          v-model.number="item.leadTime" 
                          type="number" 
                          class="form-control form-control-sm" 
                          style="font-size: 12px;"
                          @keydown.enter="saveRow(item)"
                        >
                      </td>
                      <td>
                        <input 
                          type="checkbox" 
                          v-model="item.isViewable" 
                          class="form-check-input custom-checkbox"
                          style="position: relative; display: block; margin: 0;"
                        >
                      </td>
                      <td>
                        <input 
                          type="checkbox" 
                          v-model="item.callForLeadTime" 
                          class="form-check-input custom-checkbox"
                          style="position: relative; display: block; margin: 0;"
                        >
                      </td>
                      <td>
                        <button class="btn btn-sm" @click="saveRow(item)" 
                          style="font-size: 12px; padding: 4px; margin-right: 5px; background: #4d5967; color: #fff;">
                          Save
                        </button>
                        <button class="btn btn-sm" @click="cancelEdit" 
                          style="font-size: 12px; padding: 4px; border: 1px solid #4d5967; color: #4d5967;">
                          Cancel
                        </button>
                      </td>
                    </template>

                    <!-- View mode -->
                    <template v-else>
                      <td>{{ item.productDescription }}</td>
                      <td>{{ item.footnote || '—' }}</td>
                      <td>{{ item.productCategory }}</td>
                      <td>{{ item.productFamily }}</td>
                      <td>{{ item.configuraType }}</td>
                      <td>{{ item.leadTime }}</td>
                      <td><span>{{ item.isViewable ? 'Yes' : 'No' }}</span></td>
                      <td><span>{{ item.callForLeadTime ? 'Yes' : 'No' }}</span></td>
                      <td>
                        <button class="btn btn-sm" @click="startEdit(item)" 
                          style="font-size: 12px; padding: 4px; margin-right: 5px; background: #4d5967; color: #fff;">
                          Edit
                        </button>
                        <button class="btn btn-sm" @click="deleteRow(item)" 
                          style="font-size: 12px; padding: 4px; border: 1px solid #4d5967; color: #4d5967;">
                          Delete
                        </button>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn" @click="closeEditModal" 
              style="font-size: 12px; padding: 4px; margin-right: 5px; background: #4d5967; color: #fff;">
              Close Edit Window
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- CUSTOM DELETE CONFIRMATION MODAL -->
    <div v-if="showDeleteModal" class="modal fade show" style="display: block; background: rgba(0,0,0,0.6);">
      <div class="modal-dialog modal-md">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <div @click="closeDeleteModal" style="cursor: pointer; font-weight: bold;">×</div>
          </div>
          <div class="modal-body text-center">
            <p>Are you sure you want to delete this lead time?</p>
            <strong class="d-block">{{ itemToDelete?.productDescription || '' }}</strong>
            <p class="text-danger mt-2 small">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeDeleteModal">Cancel</button>
            <button class="btn btn-danger" @click="confirmDelete">Yes, Delete</button>
          </div>
        </div>
      </div>
    </div>

    <!-- MAIN CONTENT -->
    <div class="container">
      <div class="row">
        <!-- Loading -->
        <div v-if="loading" class="col-xs-12 col-sm-12 col-12 col-lg-12">
          <div class="flex-box-wrap">
            <div class="flex-internal">
              <atom-spinner :animation-duration="1000" :size="40" color="#575757" />
              <p>Loading......</p>
            </div>
          </div>
        </div>

        <!-- No results -->
        <div v-else-if="!loading && !filteredLeadTimes.length" 
             class="col-xs-12 col-sm-12 col-12 col-lg-12 flexVHcenter">
          <div class="sorry-box">
            <img src="@/assets/img/sad.svg" />
            <h3>Sorry, no Lead Time was found...</h3>
            <button class="btn-primary" @click="firstLoad">
              Show me some Lead Times
            </button>
          </div>
        </div>

        <!-- Main Content -->
        <div v-else class="col-xs-12 col-sm-12 col-12 col-lg-12">
          
          <!-- Important note -->
          <div class="mt-3 mb-3">
            <div class="listing">
              <h5>
                IMPORTANT: Orders with special product will have additional lead
                time to allow for custom design and part setup in our system.
              </h5>
              <hr />
              <p>
                Please verify the quote you received from your Sales Support Rep
                for the amount of additional lead time to be added to your
                order. Orders with custom or special colors will have additional
                lead time to allow for color matching and paint manufacturing.
              </p>
              <p>
                Custom colors please plan for an additional 3 weeks in addition
                to standard lead time. Special Colors please plan for an
                additional 4 – 6 weeks in addition to standard lead time.
                Spacesaver will issue a formal order acknowledgment and/or
                confirmation in writing when the final ship date has been
                assigned to your order.
              </p>
            </div>
          </div>

<div v-for="(section, sIndex) in leadTimeSections" :key="section.title" class="mt-3 mb-3">
  <div class="listing">
    <h2 class="section-title">{{ section.title }}</h2>
    <div class="table-responsive">
      <table class="table-striped table-bordered lead-table">
        <thead>
          <tr>
            <th scope="col">Product</th>
            <th scope="col" class="lt-col">Lead Time</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(lt, index) in section.rows" :key="lt.id ?? index">
            <td class="product" :id="sIndex === 0 && index === 0 ? 'lead-step-1' : null">
              {{ lt.productDescription }}
            </td>
            <td class="lt-col" :id="sIndex === 0 && index === 0 ? 'lead-step-2' : null">
              <template v-if="!lt.callForLeadTime">
                <span class="weeks">{{ lt.leadTime }} {{ Number(lt.leadTime) === 1 ? "week" : "weeks" }}</span>
                <span v-if="lt.startDate || lt.endDate" class="range">{{ lt.startDate }} – {{ lt.endDate }}</span>
              </template>
              <span v-else class="call-pill">Call Customer Service</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="footnotesFor(section.rows).length" class="footnotes">
      <p v-for="(note, n) in footnotesFor(section.rows)" :key="n">{{ note }}</p>
    </div>
  </div>
</div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { AtomSpinner } from "epic-spinners";
import leadtimesTour from "../components/Tours/leadtimesTour.vue";
import LeadTimes from "../components/shared/LeadTimes.js";
import Banner2 from "../components/Banners/banner2.vue";

const auth = {
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
    Authorization: "Bearer " + localStorage.getItem("SSCUSER"),
  },
};

export default {
  name: "LeadTimes",
  components: { AtomSpinner, leadtimesTour, Banner2 },

  data() {
    return {
      ordermanageapi: import.meta.env.VITE_APP_ORDER_MANAGE_API,
      backend: import.meta.env.VITE_APP_SERVER_URL,

      // Search & filtering
      searchValue: "",
      search: "",
      storedListings: [],
      searchRender: false,
      startDate: null,
      endDate: null,
      searchStarted: false,
      acs: [],
      selectedCat: "Show All",
      acFilter: null,
      searching: false,

      // Main data
      leadtimes: [],

      // Edit Modal
      showEditModal: false,
      editedLeadTimes: [],
      editingRowId: null,
      originalRowBackup: null,

      // Delete Modal
      showDeleteModal: false,
      itemToDelete: null,

      // Other
      isSaving: false,
      loading: true,
    };
  },

  methods: {
    firstLoad: function () {
      this.loading = true;
      this.searchValue = "";
      this.startDate = null;
      this.acFilter = null;
      this.endDate = null;
      this.searchStarted = false;
      this.$store.commit("setActivePage", "order-manage");
      this.showDisclaimer?.();   // safe call in case it doesn't exist

      return axios
        .get(`${this.ordermanageapi}/leadtimes/new`, auth)
        .then((response) => {
          this.leadtimes = response.data || [];
          this.loading = false;
        })
        .catch((error) => {
          console.error(error);
          localStorage.removeItem("SSCUSER");
          this.$router.push("/");
        })
        .then(() => {
          return axios
            .get(`${this.ordermanageapi}/aclist?active=true`, auth)
            .then((response) => {
              this.acs = response.data || [];
              this.loading = false;
            });
        });
    },

    openEditModal() {
      // this.editedLeadTimes = this.leadtimes.map(item => ({ ...item }));
      this.editedLeadTimes = this.leadtimes
      .map(item => ({ ...item }))           // create shallow copy of each item
      .sort((a, b) => {
        // Sort by productCategory (string comparison)
        return a.productCategory.localeCompare(b.productCategory);
      });
      this.showEditModal = true;
      this.editingRowId = null;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingRowId = null;
      this.originalRowBackup = null;
    },

    startEdit(item) {
      this.editingRowId = item.id ?? 'new';
      this.originalRowBackup = { ...item };
    },

    cancelEdit() {
      if (this.editingRowId === 'new') {
        this.editedLeadTimes.shift();
      } else if (this.originalRowBackup) {
        const index = this.editedLeadTimes.findIndex(r => r.id === this.editingRowId);
        if (index !== -1) {
          this.editedLeadTimes.splice(index, 1, { ...this.originalRowBackup });
        }
      }
      this.editingRowId = null;
      this.originalRowBackup = null;
    },

    addNewRow() {
      const newRow = {
        id: null,
        productDescription: '',
        footnote: null,
        productCategory: '',
        productFamily: '',
        configuraType: 0,
        leadTime: 0,
        isViewable: true,
        callForLeadTime: false,
      };

      this.editedLeadTimes.unshift(newRow);
      this.$nextTick(() => {
        this.editingRowId = 'new';
      });
    },

    async saveRow(item) {
      if (this.isSaving) return;
      this.isSaving = true;

      try {
        const payload = !item.id 
          ? {
              Type: 'insert',
              Description: item.productDescription,
              ConfiguraType: item.configuraType,
              Family: item.productFamily,
              Category: item.productCategory,
              LeadTime: item.leadTime,
              isViewable: item.isViewable,
              CallForLeadTime: item.callForLeadTime,
              Footnote: item.footnote
            }
          : {
              type: 'update',
              id: item.id,
              description: item.productDescription,
              configuraType: item.configuraType,
              family: item.productFamily,
              category: item.productCategory,
              leadTime: item.leadTime,
              isViewable: item.isViewable,
              callForLeadTime: item.callForLeadTime,
              footnote: item.footnote
            };

        const response = await axios.post(
          `${this.ordermanageapi}/leadtimes${!item.id ? '' : '/'}`,
          payload,
          auth
        );

        if (response.data) {
          this.leadtimes = Array.isArray(response.data) ? response.data : this.leadtimes;
        }

        this.$toast.success(!item.id ? "Lead time created!" : "Lead time updated!");

        // this.editedLeadTimes = this.leadtimes.map(i => ({ ...i }));
        this.editedLeadTimes = this.leadtimes
        .map(item => ({ ...item }))           // create shallow copy of each item
        .sort((a, b) => {
          // Sort by productCategory (string comparison)
          return a.productCategory.localeCompare(b.productCategory);
        });
        this.editingRowId = null;
        this.originalRowBackup = null;

      } catch (err) {
        console.error(err);
        this.$toast.error("Error saving lead time.");
      } finally {
        this.isSaving = false;
      }
    },

    deleteRow(item) {
      this.itemToDelete = item;
      this.showDeleteModal = true;
    },

    async confirmDelete() {
      if (!this.itemToDelete?.id) {
        this.closeDeleteModal();
        return;
      }

      const deletepayload = { Type: 'delete', Id: this.itemToDelete.id };

      try {
        const response = await axios.post(
          `${this.ordermanageapi}/leadtimes`,
          deletepayload,
          auth
        );

        if (response.data) {
          this.leadtimes = Array.isArray(response.data) 
            ? response.data 
            : this.leadtimes.filter(l => l.id !== this.itemToDelete.id);
        } else {
          this.leadtimes = this.leadtimes.filter(l => l.id !== this.itemToDelete.id);
        }

        const index = this.editedLeadTimes.findIndex(r => r.id === this.itemToDelete.id);
        if (index !== -1) this.editedLeadTimes.splice(index, 1);

        this.$toast.success("Lead time deleted successfully!");
      } catch (err) {
        console.error(err);
        this.$toast.error("Error deleting lead time.");
      } finally {
        this.closeDeleteModal();
      }
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.itemToDelete = null;
    },

    async saveAllChanges() {
      if (!confirm('Save ALL changes to the server?')) return;

      try {
        const response = await axios.post(
          `${this.ordermanageapi}/leadtimes/bulk`, 
          this.editedLeadTimes, 
          auth
        );

        if (response.data) {
          this.leadtimes = Array.isArray(response.data) ? response.data : this.leadtimes;
          this.editedLeadTimes = this.leadtimes.map(item => ({ ...item }));
        }

        this.closeEditModal();
        this.$toast.success('All changes saved successfully!');
      } catch (err) {
        console.error(err);
        this.$toast.error('Error saving all changes.');
      }
    },

    showDisclaimer() {
      // placeholder - replace with your actual LeadTimes.showDisclaimer if needed
    },
    footnotesFor(rows) {
      // Only real footnotes, each shown once
      return [...new Set(rows.map(r => (r.footnote || "").trim()).filter(Boolean))];
    },

  },

  computed: {
    leadTimeSections() {
      return [
        { title: "ActivRAC", rows: this.ActiveRACLeadTimes },
        { title: "Mobile",   rows: this.mobileLeadTimes },
        { title: "Shelving", rows: this.shelvingLeadTimes },
        { title: "Storage",  rows: this.storageLeadTimes },
        { title: "Misc.",    rows: this.miscLeadTimes },
      ].filter(s => s.rows.length);
    },
    LeadTimesEdit() {
          return this.$store.getters.isLeadTimesEdit;
    },
    filteredLeadTimes() {
      if (this.search.trim() !== "") {
        return this.leadtimes.filter((e) => {
          return e.OrderNumber?.toLowerCase().indexOf(this.search.toLowerCase()) > -1;
        });
      } else if (this.selectedCat !== "Show All") {
        return this.leadtimes.filter((f) => f.Type == this.selectedCat);
      } else {
        return this.leadtimes;
      }
    },

    // Display sections
    mobileLeadTimes() {
      return this.leadtimes
        .filter(el => el.productCategory === "Mobile" && el.isViewable === true)
        .sort((a, b) => a.productDescription.localeCompare(b.productDescription));
    },

    shelvingLeadTimes() {
      return this.leadtimes
        .filter(el => el.productCategory === "Shelving" && el.isViewable === true)
        .sort((a, b) => a.productDescription.localeCompare(b.productDescription));
    },

    storageLeadTimes() {
      return this.leadtimes
        .filter(el => el.productCategory === "Storage" && el.isViewable === true)
        .sort((a, b) => a.productDescription.localeCompare(b.productDescription));
    },

    miscLeadTimes() {
      return this.leadtimes
        .filter(el => el.productCategory === "" && el.isViewable === true)
        .sort((a, b) => a.productDescription.localeCompare(b.productDescription));
    },

    ActiveRACLeadTimes() {
      return this.leadtimes
        .filter(el => el.productCategory === "ActivRAC" && el.isViewable === true)
        .sort((a, b) => a.productDescription.localeCompare(b.productDescription));
    },
  },

  created() {
    this.$store.commit("showNavigation");
    this.showDisclaimer = LeadTimes.showDisclaimer;
  },

  mounted() {
    this.firstLoad();
  },
};
</script>


<style scoped>
@media all and (min-width: 992px) {
  .flex-box-wrap {
    height: 200px;
    padding: 150px 0 125px 0;
    margin: 0;
  }
}

@media all and (max-width: 991px) {
  .flex-box-wrap {
    height: 100px;
    padding: 75px 0 50px 0;
    margin: 0;
    justify-content: center;
    align-items: center;
  }
}

.btn-primary:not(:disabled):not(.disabled).active,
.btn-primary:not(:disabled):not(.disabled):active,
.show > .btn-primary.dropdown-toggle {
  color: #fff;
  background-color: #00b4b4;
  border-color: #00b4b4;
}

.leadTime {
  background-color: #fff;
  line-height: 44px;
  padding: 20px;
  margin-bottom: 5px;
  border-top: 0;
}
.top-wrap {
  display: flex;
  align-items: flex-start;
}
.leadTime-bottom {
  align-items: flex-end;
}
.leadTime-header {
  background: #fff;
  color: #575757;
  display: flex !important;
}
.small-lh {
  line-height: 1.4em;
}

.listing {
  overflow: none;
}

.listing {
  background-color: #fff;
  line-height: 1;
  padding: 20px;
  margin-bottom: 5px;
  border-top: 0;
}

.table-responsive {
  display: table;
}
.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.03) !important;
}
.table-bordered td,
.table-bordered th {
  border: 1px solid #e8e8e8 !important;
}
.badge-success {
  color: #fff;
  background-color: #4d5967;
  background-color: #1eb5b6;
}
.table-striped a {
  color: #00b4b4;
}
.table-striped td,
.table-striped th {
  padding: 5px;
  font-weight: 300 !important;
}
.listing {
  border-radius: 4px;
}
table td {
  line-height: 1;
}
.table-striped th {
  background: #00b4b4;
  background: #4d5967;
  color: #fff;
}
.table-striped {
  min-width: 100%;
  overflow: scroll;
}
.search-btns {
  display: flex;
  margin-top: 10px;
  margin-bottom: 10px;
}
.search-btns button {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  border: none;
  /* background: #519bb2; */
  background: #4d5967;
  color: #fff;
  border-radius: 4px;
  padding: 5px;
  cursor: pointer;
  max-height: 40px;
}
.search-btns .clear {
  margin-left: auto;
}
.search-btns .search {
  margin-left: 5px;
}
.search-btns button span {
  font-size: 20px;
  width: 20px;
  height: 20px;
  color: #fff;
}
.search-btns button:hover {
  background: #586575;
}
.search-btns button:active {
  background: #586575;
}
.search-btns button:disabled {
  opacity: 0.6;
  cursor: unset !important;
}
.search-btns button:disabled:hover {
  background: #4d5967;
}
.search-btns button:disabled:active {
  background: #4d5967;
}

@media all and (min-width: 768px) {
  .dates {
    display: flex;
    color: #757575 !important;
  }
}
.top-search {
  color: #757575;
}
select.form-control:not([size]):not([multiple]) {
  height: auto !important;
  padding: 10px !important;
  color: #757575;
}
.all-contractors {
  color: #757575 !important;
}
.header-divide {
  border-top: 1px solid rgba(255, 255, 255, 0.5);
}
.table-striped td:last-of-type {
  text-align: right;
}
.table-striped th:last-of-type {
  text-align: right;
}
.table-bordered td:first-of-type {
  border-right: none !important;
}
.table-bordered th:first-of-type {
  border-right: none !important;
}
.table-bordered td:last-of-type {
  border-left: none !important;
}
.table-bordered th:last-of-type {
  border-left: none !important;
}

.search-btns {
  display: flex;
  margin-top: 10px;
  margin-bottom: 10px;
}
.search {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  border: none;
  /* background: #519bb2; */
  background: #4d5967;
  color: #fff;
  border-radius: 4px;
  padding: 5px;
  cursor: pointer;
  max-height: 40px;
}
.search {
  margin-left: auto;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  border: none;
  /* background: #519bb2; */
  background: #4d5967;
  color: #fff;
  border-radius: 4px;
  padding: 5px;
  cursor: pointer;
  max-height: 40px;
}
.search {
  margin-left: 5px;
}
.search span {
  font-size: 20px;
  width: 20px;
  height: 20px;
  color: #fff;
}
.search:hover {
  background: #586575;
  text-decoration: none;
}
.search:active {
  background: #586575;
  text-decoration: none;
}
.search:disabled {
  opacity: 0.6;
  cursor: unset !important;
}
.search:disabled:hover {
  background: #4d5967;
}
.search:disabled:active {
  background: #4d5967;
}
@media all and (max-width: 991px) {
  .listing {
    overflow: scroll;
  }
  table {
    width: 800px !important;
    overflow: scroll;
  }
}

.editing-row {
  height: auto !important;
  min-height: 60px;           /* Adjust this value as needed */
}

.editing-row td {
  padding: 12px 8px !important;   /* More vertical space inside cells */
  vertical-align: middle;
}

/* Make input fields taller and support multiple lines where useful */
.editing-row input.form-control {
  height: 38px;                   /* taller inputs */
  padding: 6px 10px;
}

/* Special treatment for longer fields - allow them to grow */
.editing-row td:nth-child(1),   /* Product Description */
.editing-row td:nth-child(2) {  /* Footnote */
  min-width: 220px;
}
.custom-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #4d5967;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  appearance: none;           /* Remove default browser style */
  -webkit-appearance: none;
}

.custom-checkbox:checked {
  background-color: #4d5967 !important;
  border-color: #4d5967;
}

/* Optional: Add a white checkmark when checked */
.custom-checkbox:checked::after {
  content: '✓';
  color: white;
  font-size: 14px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: bold;
}
.custom-checkbox {
  accent-color: #4d5967;     /* Modern browsers - simplest way */
  width: 20px;
  height: 20px;
}
.modal-xl{
  max-width: 90% !important;
}
@media all and (min-width: 992px) {
  .flex-box-wrap {
    height: 200px;
    padding: 150px 0 125px 0;
    margin: 0;
  }
}

@media all and (max-width: 991px) {
  .flex-box-wrap {
    height: 100px;
    padding: 75px 0 50px 0;
    margin: 0;
    justify-content: center;
    align-items: center;
  }
}

/* ... keep all your other styles ... */

.editing-row {
  height: auto !important;
  min-height: 60px;
}

.editing-row td {
  padding: 12px 8px !important;
  vertical-align: middle;
}

.editing-row input.form-control {
  height: 38px;
  padding: 6px 10px;
}

.editing-row td:nth-child(1),
.editing-row td:nth-child(2) {
  min-width: 220px;
}

/* Custom checkbox styling */
.custom-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #4d5967;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}

.custom-checkbox:checked {
  background-color: #4d5967 !important;
  border-color: #4d5967;
}

.custom-checkbox:checked::after {
  content: '✓';
  color: white;
  font-size: 14px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: bold;
}

/* Fallback for modern browsers */
.custom-checkbox {
  accent-color: #4d5967;
  width: 20px;
  height: 20px;
}

.modal-xl {
  max-width: 90% !important;
}
/* ---------- Lead time tables ---------- */
.section-title { margin: 0 0 12px; }

.lead-table { font-size: 14px; color: #2f3640; font-variant-numeric: tabular-nums; }

.lead-table th {
  background: #4d5967;
  color: #fff;
  font-size: 12px;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 10px;
  white-space: nowrap;
}
.lead-table td {
  padding: 10px;
  line-height: 1.35;
  font-weight: 400 !important;
  vertical-align: middle;
}

.lead-table td.product { color: #1f2933; }

.lead-table .lt-col { text-align: right; white-space: nowrap; width: 1%; }
.lead-table .weeks { color: #1f2933; font-weight: 600; }
.lead-table .range { color: #8a9099; margin-left: 10px; }

.call-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  background: #eceef1;
  color: #4d5967;
  font-size: 12px;
  font-weight: 500;
}

.footnotes { margin-top: 12px; }
.footnotes p {
  margin: 0 0 4px;
  font-size: 13px;
  line-height: 1.4;
  color: #6b7280;
}
.pixelsbggrid {
  background-color: #475059;
  background-size: cover;
  background-position: center;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1950 1200' preserveAspectRatio='xMidYMid slice'%3E%3Cdefs%3E%3CradialGradient id='a' cx='0' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%2356626d'/%3E%3Cstop offset='1' stop-color='%2356626d' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='b' cx='1950' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%2337778a'/%3E%3Cstop offset='1' stop-color='%2337778a' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='c' cx='975' cy='0' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%232b5c60'/%3E%3Cstop offset='1' stop-color='%232b5c60' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='d' cx='975' cy='1200' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23667787'/%3E%3Cstop offset='1' stop-color='%23667787' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='e' cx='0' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='1' stop-color='%23475059' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='f' cx='1950' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%231a8182'/%3E%3Cstop offset='1' stop-color='%231a8182' stop-opacity='0'/%3E%3C/radialGradient%3E%3ClinearGradient id='base' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='.5' stop-color='%2356626d'/%3E%3Cstop offset='1' stop-color='%231a8182'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='%23475059' width='1950' height='1200'/%3E%3Crect fill='url(%23base)' width='1950' height='1200'/%3E%3Cline x1='0' y1='0' x2='0' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='50' y1='0' x2='50' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='100' y1='0' x2='100' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='150' y1='0' x2='150' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='200' y1='0' x2='200' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='250' y1='0' x2='250' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='300' y1='0' x2='300' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='350' y1='0' x2='350' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='400' y1='0' x2='400' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='450' y1='0' x2='450' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='500' y1='0' x2='500' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='550' y1='0' x2='550' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='600' y1='0' x2='600' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='650' y1='0' x2='650' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='700' y1='0' x2='700' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='750' y1='0' x2='750' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='800' y1='0' x2='800' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='850' y1='0' x2='850' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='900' y1='0' x2='900' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='950' y1='0' x2='950' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1000' y1='0' x2='1000' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1050' y1='0' x2='1050' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1100' y1='0' x2='1100' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1150' y1='0' x2='1150' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1200' y1='0' x2='1200' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1250' y1='0' x2='1250' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1300' y1='0' x2='1300' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1350' y1='0' x2='1350' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1400' y1='0' x2='1400' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1450' y1='0' x2='1450' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1500' y1='0' x2='1500' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1550' y1='0' x2='1550' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1600' y1='0' x2='1600' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1650' y1='0' x2='1650' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1700' y1='0' x2='1700' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1750' y1='0' x2='1750' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1800' y1='0' x2='1800' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1850' y1='0' x2='1850' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1900' y1='0' x2='1900' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='0' x2='1950' y2='0' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='50' x2='1950' y2='50' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='100' x2='1950' y2='100' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='150' x2='1950' y2='150' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='200' x2='1950' y2='200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='250' x2='1950' y2='250' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='300' x2='1950' y2='300' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='350' x2='1950' y2='350' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='400' x2='1950' y2='400' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='450' x2='1950' y2='450' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='500' x2='1950' y2='500' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='550' x2='1950' y2='550' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='600' x2='1950' y2='600' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='650' x2='1950' y2='650' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='700' x2='1950' y2='700' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='750' x2='1950' y2='750' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='800' x2='1950' y2='800' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='850' x2='1950' y2='850' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='900' x2='1950' y2='900' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='950' x2='1950' y2='950' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1000' x2='1950' y2='1000' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='1050' x2='1950' y2='1050' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1100' x2='1950' y2='1100' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1150' x2='1950' y2='1150' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cg fill-opacity='0.45'%3E%3Crect fill='url(%23a)' width='1950' height='1200'/%3E%3Crect fill='url(%23b)' width='1950' height='1200'/%3E%3Crect fill='url(%23c)' width='1950' height='1200'/%3E%3Crect fill='url(%23d)' width='1950' height='1200'/%3E%3Crect fill='url(%23e)' width='1950' height='1200'/%3E%3Crect fill='url(%23f)' width='1950' height='1200'/%3E%3C/g%3E%3C/svg%3E");
}
.table-striped tbody tr:nth-of-type(odd) {
   background-color: rgb(241 246 249 / 60%) !important
}
</style>